import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loginWithEmail, sendCustomPasswordReset } from '../firebase/auth';
import { currentProject, isMultiProjectMode } from '../firebase/index';
import './Login.css';

const PinPad = ({ pin, onPinChange, onSubmit, disabled }) => {
  const handleNum = (n) => {
    if (pin.length < 6) onPinChange(pin + n);
  };
  const handleBack = () => onPinChange(pin.slice(0, -1));
  const handleClear = () => onPinChange('');

  const digits = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['⌫', '0', 'C'],
  ];

  return (
    <div className="pin-pad">
      <div className="pin-dots">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <span key={i} className={pin.length > i ? 'pin-dot filled' : 'pin-dot'} />
        ))}
      </div>
      <div className="pin-grid">
        {digits.flat().map((key) => (
          <button
            key={key}
            type="button"
            className="pin-key"
            disabled={disabled}
            onClick={() => {
              if (key === '⌫') handleBack();
              else if (key === 'C') handleClear();
              else handleNum(key);
            }}
          >
            {key}
          </button>
        ))}
      </div>
      <button
        type="button"
        className="login-btn"
        disabled={pin.length !== 6 || disabled}
        onClick={onSubmit}
      >
        {disabled ? '...' : 'Sign In'}
      </button>
    </div>
  );
};

const Login = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('worker');
  const [email, setEmail] = useState(() => localStorage.getItem('fieldsync_last_email') || '');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Dynamic company branding
  const companyName = currentProject?.companyName || 'Field Sync';
  const companyLogo = currentProject?.companyLogo || '/logo.png';
  const isMultiCompany = isMultiProjectMode();

  const handleAdminSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const { error: err } = await loginWithEmail(email, password);
    if (err) setError(err);
    else {
      localStorage.setItem('fieldsync_last_email', email);
      navigate('/');
    }
    setLoading(false);
  };

  const handleWorkerSubmit = async () => {
    setError('');
    setLoading(true);
    const { error: err } = await loginWithEmail(email, pin);
    if (err) setError(err);
    else {
      localStorage.setItem('fieldsync_last_email', email);
      navigate('/');
    }
    setLoading(false);
  };

  const clearSavedEmail = () => {
    localStorage.removeItem('fieldsync_last_email');
    setEmail('');
  };

  const hasSavedEmail = !!localStorage.getItem('fieldsync_last_email');

  return (
    <div className="login-page">
      <div className="login-card">
        <img src={companyLogo} alt={companyName} className="login-logo-img" />
        <h1 className="login-wordmark">{companyName}</h1>
        {isMultiCompany && (
          <p className="login-company-indicator" style={{ fontSize: '0.85rem', color: '#666', marginTop: '-0.5rem' }}>
            🔒 {companyName} Portal
          </p>
        )}

        {/* Mode Toggle */}
        <div className="login-mode-toggle">
          <button
            type="button"
            className={mode === 'worker' ? 'mode-btn active' : 'mode-btn'}
            onClick={() => { setMode('worker'); setError(''); setPassword(''); setPin(''); }}
          >
            🔧 Field Worker
          </button>
          <button
            type="button"
            className={mode === 'admin' ? 'mode-btn active' : 'mode-btn'}
            onClick={() => { setMode('admin'); setError(''); setPassword(''); setPin(''); }}
          >
            🛡️ Admin / Manager
          </button>
        </div>

        {/* Email input (shared) */}
        <div className="email-row">
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="login-email-input"
          />
          {hasSavedEmail && (
            <button type="button" className="clear-email-btn" onClick={clearSavedEmail} title="Not you?">
              ✕
            </button>
          )}
        </div>

        {/* Admin mode */}
        {mode === 'admin' && (
          <form onSubmit={handleAdminSubmit} className="login-form">
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
            />
            {error && <p className="login-error">{error}</p>}
            <button type="submit" className="login-btn" disabled={loading}>
              {loading ? '...' : 'Sign In'}
            </button>
            <button
              type="button"
              className="login-forgot-btn"
              onClick={async () => {
                if (!email) { setError('Enter your email first'); return; }
                setLoading(true);
                const { success, error: err } = await sendCustomPasswordReset(email);
                if (success) setError('Check your email for password reset link');
                else setError(err || 'Failed to send reset email');
                setLoading(false);
              }}
            >
              Forgot password?
            </button>
          </form>
        )}

        {/* Worker mode */}
        {mode === 'worker' && (
          <>
            {error && <p className="login-error">{error}</p>}
            <PinPad pin={pin} onPinChange={setPin} onSubmit={handleWorkerSubmit} disabled={loading} />
          </>
        )}

        <p className="login-help">
          New user? Contact your admin for access.
        </p>
      </div>
    </div>
  );
};

export default Login;
